import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { useAuth } from "@/auth/auth-provider";
import { Button } from "@/components/ui/button";
import { permissionLabel } from "@/lib/permission-labels";
import {
  getAllowedRecoveryRoute,
  type RouteAccessRequirement,
} from "@/lib/route-access";

export function PermissionDenied({
  requirement,
}: {
  requirement: RouteAccessRequirement;
}) {
  const { access, employee } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const recovery = getAllowedRecoveryRoute(access, location.pathname);
  const blocked = new Set(
    employee?.permissionOverrides
      ?.filter((item) => item.effect === "DENY")
      .map((item) => item.permission.code),
  );
  return (
    <section
      className="mx-auto max-w-2xl space-y-4 rounded-xl border p-6"
      aria-labelledby="permission-denied-title"
    >
      <ShieldAlert
        className="size-8 text-muted-foreground"
        aria-hidden="true"
      />
      <h1 id="permission-denied-title" className="text-xl font-semibold">
        You don’t have access to {requirement.title.toLowerCase()}
      </h1>
      <p className="text-sm text-muted-foreground">
        {requirement.anyOf.length === 1
          ? "This page requires the following capability:"
          : "This page requires at least one of these capabilities:"}
      </p>
      <ul className="space-y-3">
        {requirement.anyOf.map((code) => (
          <li key={code} className="rounded-lg bg-muted/30 p-3 text-sm">
            <p className="font-medium">{permissionLabel({ code })}</p>
            <p className="text-xs text-muted-foreground">{code}</p>
            <p className="mt-1 text-muted-foreground">
              {blocked.has(code)
                ? "An individual exception blocks this action, even if a role grants it."
                : "Your current roles and individual exceptions do not grant this action."}
            </p>
          </li>
        ))}
      </ul>
      {requirement.roleAlternative ? (
        <p className="text-sm text-muted-foreground">
          Alternatively, this page is available to {requirement.roleAlternative}
          .
        </p>
      ) : null}
      <p className="text-sm">
        Ask a workspace administrator to review your roles and individual
        exceptions if you need this access.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={() => navigate(-1)}>
          Go back
        </Button>
        {recovery ? (
          <Button asChild>
            <Link to={recovery.path}>Open {recovery.label}</Link>
          </Button>
        ) : null}
      </div>
    </section>
  );
}
